import React from 'react';
import { config } from './config';
import { ProgressiveImage } from './ProgressiveImage';

// Define the types for Strapi blocks
export interface StrapiBlock {
  type: string;
  children?: StrapiInlineNode[];
  level?: number;
  format?: 'ordered' | 'unordered';
  image?: {
    name: string;
    url: string;
    alternativeText?: string;
    caption?: string;
    width?: number;
    height?: number;
    formats?: {
      xsmall?: { url: string };
      thumbnail?: { url: string };
      small?: { url: string };
      medium?: { url: string };
      large?: { url: string };
      xlarge?: { url: string };
    };
  };
}

export interface StrapiInlineNode {
  type: 'text' | 'link';
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  url?: string;
  children?: StrapiInlineNode[];
}

interface BlocksRendererProps {
  content: StrapiBlock[];
  className?: string;
}

const BlocksRenderer: React.FC<BlocksRendererProps> = ({ content, className = '' }) => {
  if (!content || !Array.isArray(content)) {
    return null;
  }

  const renderInlineNodes = (nodes: StrapiInlineNode[]): React.ReactNode => {
    return nodes?.map((node, index) => {
      if (node.type === 'text') {
        let element = <span key={index}>{node.text}</span>;

        if (node.bold) element = <strong key={index}>{element}</strong>;
        if (node.italic) element = <em key={index}>{element}</em>;
        if (node.underline) element = <u key={index}>{element}</u>;
        if (node.strikethrough) element = <del key={index}>{element}</del>;
        if (node.code) element = <code key={index} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{element}</code>;

        return element;
      }

      if (node.type === 'link') {
        return (
          <a
            key={index}
            href={node.url}
            className="text-geraniumSylvaticum hover:text-shadyLeaf underline transition-colors duration-200"
            target="_blank"
            rel="noopener noreferrer"
          >
            {node.children && renderInlineNodes(node.children)}
          </a>
        );
      }

      return null;
    });
  };

  const renderBlock = (block: StrapiBlock, index: number): React.ReactNode => {
    switch (block.type) {
      case 'paragraph':
        return (
          <p key={index} className="mb-4 text-gray-700 leading-relaxed">
            {block.children && renderInlineNodes(block.children)}
          </p>
        );

      case 'heading':
        const level = block.level || 1;
        const headingClasses = {
          1: 'text-3xl font-bold text-shadyLeaf mb-6 mt-8',
          2: 'text-2xl font-semibold text-shadyLeaf mb-5 mt-7',
          3: 'text-xl font-semibold text-shadyLeaf mb-4 mt-6',
          4: 'text-lg font-semibold text-shadyLeaf mb-3 mt-5',
          5: 'text-base font-semibold text-shadyLeaf mb-3 mt-4',
          6: 'text-sm font-semibold text-shadyLeaf mb-2 mt-3',
        }[level] || 'text-lg font-semibold text-shadyLeaf mb-3 mt-5';

        const content = block.children && renderInlineNodes(block.children);

        if (level === 1) return <h1 key={index} className={headingClasses}>{content}</h1>;
        if (level === 2) return <h2 key={index} className={headingClasses}>{content}</h2>;
        if (level === 3) return <h3 key={index} className={headingClasses}>{content}</h3>;
        if (level === 4) return <h4 key={index} className={headingClasses}>{content}</h4>;
        if (level === 5) return <h5 key={index} className={headingClasses}>{content}</h5>;
        return <h6 key={index} className={headingClasses}>{content}</h6>;

      case 'list':
        const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
        const listClasses = block.format === 'ordered'
          ? 'list-decimal list-inside mb-4 ml-4 space-y-2'
          : 'list-disc list-inside mb-4 ml-4 space-y-2';

        return (
          <ListTag key={index} className={listClasses}>
            {block.children?.map((item, itemIndex) => (
              <li key={itemIndex} className="text-gray-700 leading-relaxed">
                {renderInlineNodes(item.children || [])}
              </li>
            ))}
          </ListTag>
        );

      case 'quote':
        return (
          <blockquote key={index} className="rounded-lg bg-gray-100 p-4 my-6">
            <div className="text-gray-600 leading-relaxed">
              {block.children && renderInlineNodes(block.children)}
            </div>
          </blockquote>
        );

      case 'code':
        return (
          <pre key={index} className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4">
            <code className="font-mono text-sm">
              {block.children && renderInlineNodes(block.children)}
            </code>
          </pre>
        );

      case 'image':
        const baseUrl = config.API_URL.replace("/api", "");
        // Only treat images from OTHER domains as external, not our own Strapi images
        const isExternalImage = block.image?.url?.startsWith('http') && !block.image?.url?.startsWith(baseUrl);

        // Get low and high quality URLs
        const getLowQualityUrl = () => {
          if (isExternalImage) return block.image?.url || '';

          const formats = block.image?.formats;

          // Use thumbnail for low quality
          const thumbnailUrl = formats?.thumbnail?.url;
          if (thumbnailUrl) {
            return `${baseUrl}${thumbnailUrl}`;
          }

          // Fallback to small
          const smallUrl = formats?.small?.url;
          if (smallUrl) {
            return `${baseUrl}${smallUrl}`;
          }

          // Fallback to original
          return `${baseUrl}${block.image?.url}`;
        };

        const getHighQualityUrl = () => {
          if (isExternalImage) return block.image?.url || '';
          const large = block.image?.formats?.large?.url;
          const medium = block.image?.formats?.medium?.url;
          return large
            ? `${baseUrl}${large}`
            : medium
              ? `${baseUrl}${medium}`
              : `${baseUrl}${block.image?.url}`;
        };

        const lowQualityUrl = getLowQualityUrl();
        const highQualityUrl = getHighQualityUrl();

        return (
          <div key={index} className="my-6">
            <div className="inline-block">
              <ProgressiveImage
                lowQualitySrc={lowQualityUrl}
                highQualitySrc={highQualityUrl}
                alt={block.image?.alternativeText || block.image?.name || ''}
                className="rounded"
                width={block.image?.width}
                height={block.image?.height}
              />
              {block.image?.caption && (
                <p className="text-sm text-gray-600 mt-2 italic text-center">
                  {block.image.caption}
                </p>
              )}
            </div>
          </div>
        );

      default:
        console.warn(`Unknown block type: ${block.type}`, block);
        return null;
    }
  };

  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      {content.map((block, index) => renderBlock(block, index))}
    </div>
  );
};

export default BlocksRenderer;